import { IDBPDatabase, openDB, deleteDB } from 'idb';
import "core-js/actual";
import "regenerator-runtime/runtime";

// Ensure global indexedDB exists to prevent ReferenceErrors in environments like Safari Lockdown Mode
if (typeof window !== 'undefined' && !('indexedDB' in window)) {
  (window as any).indexedDB = null;
}

const isSupported = typeof window !== 'undefined' && !!window.indexedDB;

class IndexedDb {
  private database: string;
  private db: any;
  private storeVersion: number = 4;
  private tableNames: string[];
  
  // In-memory fallback database
  private isInMemory: boolean = false;
  private memoryStores: { [tableName: string]: { [key: string]: any } } = {};
  private memoryAutoIncrementIds: { [tableName: string]: number } = {};

  constructor(database: string, tableNames: string[] = []) {
    this.database = database;
    this.tableNames = tableNames;
    if (!isSupported) {
      this.isInMemory = true;
      console.warn('IndexedDB is not supported in this environment. Falling back to in-memory storage.');
      for (const name of tableNames) {
        this.memoryStores[name] = {};
        this.memoryAutoIncrementIds[name] = 0;
      }
    }
  }

  private async getDb() {
    if (this.isInMemory) {
      return null;
    }
    if (this.db) {
      return this.db;
    }

    this.db = await openDB(this.database, this.storeVersion, {
      upgrade: (db: IDBPDatabase) => {
        for (const tableName of this.tableNames) {
          console.log('IndexedDb::upgrade creating/checking object store ' + tableName);
          if (!db.objectStoreNames.contains(tableName)) {
            db.createObjectStore(tableName, { autoIncrement: true });
          }
        }
      },
      blocked() {
        console.log('IndexedDb::getDb blocked')
      },
      terminated() {
        console.log('IndexedDb::getDb terminated')
      },
      blocking() {
        console.log('IndexedDb::getDb blocking')
        return true;
      }
    });

    return this.db;
  }

  public async delete() {
    if (this.isInMemory) {
      this.memoryStores = {};
      this.memoryAutoIncrementIds = {};
      return true;
    }
    try {
      return await deleteDB(this.database, {
        blocked() {
          console.log('IndexedDb::delete blocked');
        }
      })
        .then(() => {
          console.log('deleted db ' + this.database)
          return true;
        }).catch(err => console.error(err));
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  public async createObjectStore(tableNames: string[]) {
    this.tableNames = tableNames;
    if (this.isInMemory) {
      for (const name of tableNames) {
        if (!this.memoryStores[name]) {
          this.memoryStores[name] = {};
          this.memoryAutoIncrementIds[name] = 0;
        }
      }
      return true;
    }
    try {
      this.db = await this.getDb();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  public async getValue(tableName: string, id: number) {
    if (this.isInMemory) {
      return this.memoryStores[tableName]?.[id] || null;
    }
    const db = await this.getDb();
    const tx = db.transaction(tableName, 'readonly');
    const store = tx.objectStore(tableName);
    const result = await store.get(id);
    console.log('IndexedDb::getValue', 'Fetched Data from db', { result });
    return result;
  }

  public async getAllValue(tableName: string) {
    if (this.isInMemory) {
      return Object.values(this.memoryStores[tableName] || {});
    }
    const db = await this.getDb();
    const tx = db.transaction(tableName, 'readonly');
    const store = tx.objectStore(tableName);
    const result = await store.getAll();
    return result;
  }

  public async getAllKeys(tableName: string) {
    if (this.isInMemory) {
      return Object.keys(this.memoryStores[tableName] || {}).map(Number);
    }
    console.log('IndexedDb::getAllKeys');
    const db = await this.getDb();
    const tx = db.transaction(tableName, 'readonly');
    const store = tx.objectStore(tableName);
    const result = await store.getAllKeys();
    return result;
  }

  public async putValue(tableName: string, value: object, key?: string) {
    if (this.isInMemory) {
      if (!this.memoryStores[tableName]) {
        this.memoryStores[tableName] = {};
        this.memoryAutoIncrementIds[tableName] = 0;
      }
      const id = key !== undefined ? key : ++this.memoryAutoIncrementIds[tableName];
      this.memoryStores[tableName][id] = value;
      return id;
    }
    const db = await this.getDb();
    const tx = db.transaction(tableName, 'readwrite');
    const store = tx.objectStore(tableName);
    return await store.put(value, key);
  }

  public async putBulkValue(tableName: string, values: object[]) {
    if (this.isInMemory) {
      for (const value of values) {
        await this.putValue(tableName, value);
      }
      return this.getAllValue(tableName);
    }
    const db = await this.getDb();
    const tx = db.transaction(tableName, 'readwrite');
    const store = tx.objectStore(tableName);
    for (const value of values) {
      const result = await store.put(value);
      console.log('IndexedDb::putBulkValue', 'Put Bulk Data ', JSON.stringify(result));
    }
    return this.getAllValue(tableName);
  }

  public async count(tableName: string) {
    if (this.isInMemory) {
      return Object.keys(this.memoryStores[tableName] || {}).length;
    }
    const db = await this.getDb();
    const store = db.transaction(tableName).store;
    const count = await store.count();
    return count;
  }

  public async getCursor(tableName: string) {
    if (this.isInMemory) {
      const store = this.memoryStores[tableName] || {};
      const keys = Object.keys(store).map(Number);
      const makeCursor = (idx: number): any => {
        if (idx >= keys.length) return null;
        const key = keys[idx];
        return {
          key,
          value: store[key],
          continue: async () => makeCursor(idx + 1)
        };
      };
      return makeCursor(0);
    }
    const db = await this.getDb();
    return await db.transaction(tableName).store.openCursor();
  }

  public async deleteValue(tableName: string, id: any) {
    if (this.isInMemory) {
      if (this.memoryStores[tableName] && id in this.memoryStores[tableName]) {
        delete this.memoryStores[tableName][id];
        console.log('IndexedDb::deleteValue', 'Deleted Data', id);
        return id;
      }
      console.log('IndexedDb::deleteValue', 'Id not found', id);
      return null;
    }
    const db = await this.getDb();
    const tx = db.transaction(tableName, 'readwrite');
    const store = tx.objectStore(tableName);
    const result = await store.get(id);
    if (!result) {
      console.log('IndexedDb::deleteValue', 'Id not found', id);
      return result;
    }
    await store.delete(id);
    console.log('IndexedDb::deleteValue', 'Deleted Data', id);
    return id;
  }
}

export default IndexedDb;
